import {
    collection,
    deleteDoc,
    deleteField,
    doc,
    getDocs,
    query,
    updateDoc,
    where,
    writeBatch,
    type Firestore,
} from 'firebase/firestore';
import type { Chef, Language, Recipe, UserMealPreferences } from '../types';
import { normalizeChefPayload } from './chefCsv';
import { buildChefMergePatch } from './chefMerge';
import { getChefDisplayName } from './chefDisplay';
import { getTranslation } from './translations';

const BATCH_SIZE = 400;

export interface MergeChefsResult {
    recipesUpdated: number;
    usersUpdated: number;
    favoritePrefsUpdated: number;
    requestsUpdated: number;
}

export async function mergeChefsInFirestore(
    db: Firestore,
    keepId: string,
    mergeId: string,
    keepChef: Chef,
    mergeChef: Chef,
): Promise<MergeChefsResult> {
    if (keepId === mergeId) {
        throw new Error('Cannot merge a chef into itself.');
    }

    const patch = normalizeChefPayload(buildChefMergePatch(keepChef, mergeChef));
    await writeBatch(db).update(doc(db, 'chefs', keepId), patch).commit();

    let recipesUpdated = 0;
    const recipeSnap = await getDocs(query(collection(db, 'recipes'), where('chefId', '==', mergeId)));
    const recipeDocs = recipeSnap.docs;
    for (let i = 0; i < recipeDocs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        for (const recipeDoc of recipeDocs.slice(i, i + BATCH_SIZE)) {
            batch.update(recipeDoc.ref, { chefId: keepId });
            recipesUpdated++;
        }
        await batch.commit();
    }

    let usersUpdated = 0;
    const userSnap = await getDocs(query(collection(db, 'users'), where('chefId', '==', mergeId)));
    for (let i = 0; i < userSnap.docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        for (const userDoc of userSnap.docs.slice(i, i + BATCH_SIZE)) {
            batch.update(userDoc.ref, { chefId: keepId });
            usersUpdated++;
        }
        await batch.commit();
    }

    let favoritePrefsUpdated = 0;
    const prefsSnap = await getDocs(collection(db, 'userMealPreferences'));
    for (const prefsDoc of prefsSnap.docs) {
        const prefs = prefsDoc.data() as UserMealPreferences;
        const chefFavorites = prefs.favorites?.chefs ?? {};
        if (!chefFavorites[mergeId]) continue;

        const keepFavorite = chefFavorites[keepId];
        const mergeFavorite = chefFavorites[mergeId];
        await updateDoc(prefsDoc.ref, {
            [`favorites.chefs.${keepId}`]: keepFavorite ?? mergeFavorite,
            [`favorites.chefs.${mergeId}`]: deleteField(),
            updatedAt: new Date().toISOString(),
        });
        favoritePrefsUpdated++;
    }

    let requestsUpdated = 0;
    const requestSnap = await getDocs(query(collection(db, 'chefRequests'), where('targetChefId', '==', mergeId)));
    for (let i = 0; i < requestSnap.docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        for (const requestDoc of requestSnap.docs.slice(i, i + BATCH_SIZE)) {
            batch.update(requestDoc.ref, { targetChefId: keepId });
            requestsUpdated++;
        }
        await batch.commit();
    }

    await deleteDoc(doc(db, 'chefs', mergeId));

    return { recipesUpdated, usersUpdated, favoritePrefsUpdated, requestsUpdated };
}

export function countRecipesForChef(recipes: Recipe[], chefId: string): number {
    return recipes.filter((recipe) => !recipe.isDeleted && recipe.chefId === chefId).length;
}

export function formatMergeSummary(
    result: MergeChefsResult,
    mergeChef: Chef,
    keepChef: Chef,
    language: Language = 'en',
): string {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const mergedName = getChefDisplayName(mergeChef, language);
    const keptName = getChefDisplayName(keepChef, language);

    const lines = [
        t('mergeSuccessSummary')
            .replace('{merged}', mergedName)
            .replace('{kept}', keptName),
        t('mergeRecipesUpdated').replace('{count}', String(result.recipesUpdated)),
    ];

    if (result.usersUpdated > 0) {
        lines.push(t('mergeUsersUpdated').replace('{count}', String(result.usersUpdated)));
    }
    if (result.favoritePrefsUpdated > 0) {
        lines.push(t('mergeFavoritesUpdated').replace('{count}', String(result.favoritePrefsUpdated)));
    }

    return lines.join('\n');
}
